// Environment resolution for OZIRA Web (spec section 40).
import { resolveEnvironment, selectConfig } from 'ozira-app-core';

export interface AppConfig {
  apiBase: string;
  supabaseUrl: string;
  supabasePublishableKey: string;
  productUrls: Record<string, string>;
}

// Cross-product URLs for OziraProductSwitcher (Gate 10). Each product repo
// keeps its own copy of this map, same convention as apiBase/supabaseUrl
// above -- there is no shared runtime config service this phase.
const STAGING_PRODUCT_URLS: Record<string, string> = {
  assistant: 'https://ai.staging.ozira.et',
  code: 'https://code.staging.ozira.et',
  design: 'https://design.staging.ozira.et',
  p: 'https://p.staging.ozira.et',
  forward: 'https://forward.staging.ozira.et',
};

const STAGING_RAILWAY_API_BASE = 'https://ozira-ai-staging-staging.up.railway.app';

const PRODUCTION_PRODUCT_URLS: Record<string, string> = {
  assistant: 'https://www.ozira.et/chat/',
  code: 'https://www.ozira.et/code/',
  design: 'https://www.ozira.et/design/',
  p: 'https://www.ozira.et/cowork/',
  forward: 'https://www.ozira.et/forward/',
};

// Non-secret identifiers only (a URL and a Supabase *publishable* key --
// the browser-safe key, not the service role key). See section 18: never
// a service credential in a client.
const STAGING: AppConfig = {
  // The custom api.staging.ozira.et DNS record is not currently resolvable
  // from browser clients. Use the active Railway service domain so local and
  // staging clients can reach the same API until that DNS record is restored.
  apiBase: 'https://api.staging.ozira.et',
  supabaseUrl: 'https://gdydxlmidnshluvpynml.supabase.co',
  supabasePublishableKey: 'sb_publishable_37lfDC8N6u__YtvtXQFmSQ_B4Y3Teh5',
  productUrls: STAGING_PRODUCT_URLS,
};

export function resolveProductionApiBase(origin?: string): string {
  try {
    const url = new URL(origin || 'https://www.ozira.et');
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return 'https://www.ozira.et';
    return url.origin.replace(/\/+$/, '');
  } catch {
    return 'https://www.ozira.et';
  }
}

export function resolveStagingApiBase(origin?: string, hostname?: string): string {
  const normalizedHostname = hostname?.toLowerCase();
  if (origin && normalizedHostname?.endsWith('.up.railway.app')) {
    return origin.replace(/\/+$/, '');
  }
  return STAGING_RAILWAY_API_BASE;
}

const DEVELOPMENT: AppConfig = {
  // Local dev talks to the same staging backend by default -- there is no
  // separate local backend to run. Override via VITE_OZIRA_API_BASE if needed.
  apiBase: import.meta.env.VITE_OZIRA_API_BASE || STAGING_RAILWAY_API_BASE,
  supabaseUrl: import.meta.env.VITE_OZIRA_SUPABASE_URL || STAGING.supabaseUrl,
  supabasePublishableKey: import.meta.env.VITE_OZIRA_SUPABASE_PUBLISHABLE_KEY || STAGING.supabasePublishableKey,
  productUrls: STAGING_PRODUCT_URLS,
};

const CONFIG_BY_ENVIRONMENT: Record<string, AppConfig> = {
  development: DEVELOPMENT,
  staging: {
    ...STAGING,
    // The Railway-generated staging URL serves the UI and API from one
    // service. Use that reachable same-origin API when the custom staging DNS
    // name is unavailable; custom staging hosts keep the canonical API domain.
    apiBase: resolveStagingApiBase(
      typeof window !== 'undefined' ? window.location.origin : undefined,
      typeof window !== 'undefined' ? window.location.hostname : undefined,
    ),
  },
  // Production serves the API from the same Railway service as the UI. Keeping
  // this same-origin avoids sending authenticated browser requests to staging,
  // which is not CORS-authorized for ai.ozira.et.
  production: {
    ...STAGING,
    productUrls: PRODUCTION_PRODUCT_URLS,
    apiBase: resolveProductionApiBase(typeof window !== 'undefined' ? window.location.origin : undefined),
    // Production auth configuration is deployment-owned and must come from
    // /api/auth-config. Empty values make a failed lookup fail closed rather
    // than sending credentials to the staging Supabase project.
    supabaseUrl: '',
    supabasePublishableKey: '',
  },
};

export function getEnvironment(): 'development' | 'staging' | 'production' {
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : undefined;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname?.endsWith('.local');

  // A build-time override is useful for local verification, but it must never
  // make a deployed staging or production hostname present itself as a
  // development environment.
  if (hostname && !isLocal) {
    return hostname.includes('staging') ? 'staging' : 'production';
  }

  return resolveEnvironment({
    explicit: import.meta.env.VITE_OZIRA_ENVIRONMENT,
    hostname,
  }) as 'development' | 'staging' | 'production';
}

export function getConfig(): AppConfig {
  return selectConfig(CONFIG_BY_ENVIRONMENT, getEnvironment());
}
